import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { MinimizedPlayer } from './minimized-player';
import { MaximizedPlayer } from './maximized-player';
import {
  onBeatRegister, onBeatUnregister, onBeatState,
  cmdPlay, cmdPause, cmdSeek, cmdVolume
} from './player-bus';

export function ExpandableProPlayer({ className = '' }) {
  const [isMaximized, setIsMaximized] = useState(false);

  // Список карточек (регистрируются BeatItem’ами)
  const [beats, setBeats] = useState([]); // [{id,title,artist,cover}]
  useEffect(() => {
    const offReg = onBeatRegister((b) => {
      setBeats((prev) => (prev.find(x => x.id === b.id) ? prev : [...prev, b]));
    });
    const offUnreg = onBeatUnregister(({ id }) => {
      setBeats((prev) => prev.filter(x => x.id !== id));
    });
    return () => { offReg(); offUnreg(); };
  }, []);

  // Текущее состояние, которое присылает активный BeatItem
  const [activeId, setActiveId] = useState(null);
  const [playing, setPlaying]   = useState(false);
  const [currentTime, setCT]    = useState(0);
  const [duration, setDur]      = useState(0);

  useEffect(() => {
    const off = onBeatState(({ id, playing, currentTime, duration }) => {
      if (playing) setActiveId(id); // кто-то заиграл — он активный
      if (!activeId || activeId === id) {
        setPlaying(!!playing);
        setCT(currentTime || 0);
        setDur(duration || 0);
      }
    });
    return off;
  }, [activeId]);

  const currentIndex = useMemo(
    () => beats.findIndex(b => b.id === activeId),
    [beats, activeId]
  );
  const currentTrack = currentIndex >= 0 ? beats[currentIndex] : null;

  // Громкость (UI) — пушим в активный BeatItem
  const [volume, setVolume] = useState(1);
  const [isMuted, setMuted] = useState(false);
  useEffect(() => {
    if (activeId != null) cmdVolume(activeId, isMuted ? 0 : volume);
  }, [activeId, volume, isMuted]);

  // Управление
  const handleTogglePlayPause = useCallback(() => {
    if (activeId == null) {
      if (beats[0]) cmdPlay(beats[0].id);
      return;
    }
    playing ? cmdPause(activeId) : cmdPlay(activeId);
  }, [activeId, playing, beats]);

  const handleNext = useCallback(() => {
    if (!beats.length) return;
    const idx = currentIndex < 0 ? 0 : (currentIndex + 1) % beats.length;
    cmdPlay(beats[idx].id);
  }, [beats, currentIndex]);

  const handlePrevious = useCallback(() => {
    if (!beats.length) return;
    const idx = currentIndex < 0 ? 0 : (currentIndex - 1 + beats.length) % beats.length;
    cmdPlay(beats[idx].id);
  }, [beats, currentIndex]);

  const handleSeek = useCallback((pct) => {
    if (!duration || activeId == null) return;
    cmdSeek(activeId, (pct / 100) * duration);
  }, [activeId, duration]);

  const handleVolumeChange = useCallback((v) => {
    setVolume(v);
    setMuted(v === 0);
  }, []);
  const toggleMute      = useCallback(() => setMuted((m) => !m), []);
  const toggleRepeat    = useCallback(() =>
    setRepeatMode((m) => (m === 'none' ? 'all' : m === 'all' ? 'one' : 'none')), []);
  const toggleShuffle   = useCallback(() => setShuffled(s => !s), []);
  const toggleMaximize  = useCallback(() => setIsMaximized(m => !m), []);

  const [repeatMode, setRepeatMode] = useState('none'); // локальные режимы (опционально)
  const [isShuffled, setShuffled]   = useState(false);

  const progress = duration ? (currentTime / duration) * 100 : 0;

  const playerProps = {
    currentTrack,
    isPlaying: playing,
    isLoading: false,
    error: null,
    volume,
    isMuted,
    repeatMode,
    isShuffled,
    progress,
    duration,
    currentTime,
    canPlay: true,
    tracks: beats,
    currentTrackIndex: Math.max(0, currentIndex),
    onTogglePlayPause: handleTogglePlayPause,
    onNext: handleNext,
    onPrevious: handlePrevious,
    onSeek: handleSeek,
    onVolumeChange: handleVolumeChange,
    onToggleMute: toggleMute,
    onToggleRepeat: toggleRepeat,
    onToggleShuffle: toggleShuffle,
    onToggleMaximize: toggleMaximize,
    onTrackSelect: (idx) => { const t = beats[idx]; if (t) cmdPlay(t.id); },
  };

  if (!beats.length && !currentTrack) return null;

  return (
    <div className={className}>
      {isMaximized ? (
        <MaximizedPlayer {...playerProps} />
      ) : (
        <MinimizedPlayer {...playerProps} />
      )}
    </div>
  );
}

